import { useState, useEffect } from 'react'
import { useRouter } from 'next/router'
import { useTeamsSDK } from 'use-teams-sdk'

const detectInitialMobile = () => {
  if (process.env.NEXT_PUBLIC_FORCE_DESKTOP === 'true') return false
  if (typeof window === 'undefined') return false

  const userAgent =
    window.navigator.userAgent || window.navigator.vendor || (window as any).opera
  const mobileUserAgent =
    /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
      userAgent
    )
  const compactTaskModule = window.innerWidth <= 800 && window.innerHeight <= 900

  return mobileUserAgent || compactTaskModule
}

const detectTeamsContext = () => {
  if (typeof window === 'undefined') return false

  const url = window.location.href
  return (
    /teamstab|teamschat|messenger|taskModule/.test(url) ||
    Boolean(window.name && /embedded|iframe/.test(window.name)) ||
    typeof (window as any).microsoftTeams !== 'undefined' ||
    document.documentElement.classList.contains('teams-dark') ||
    document.documentElement.classList.contains('teams-light')
  )
}

/**
 * Detects mobile Teams clients, with browser/mobile fallback for task modules.
 */
export function useTeamsMobile() {
  const [isMobile, setIsMobile] = useState(
    () => detectInitialMobile() && detectTeamsContext()
  )
  const router = useRouter()
  const teamsSDKPromise = useTeamsSDK()

  useEffect(() => {
    if (process.env.NEXT_PUBLIC_FORCE_DESKTOP === 'true') {
      setIsMobile(false)
      return
    }

    if (router.query.mobile === 'true' || router.query.mobile === '1') {
      setIsMobile(true)
      return
    }

    const detectMobileBrowser = () => {
      if (typeof window === 'undefined') return false

      const userAgent =
        window.navigator.userAgent || window.navigator.vendor || (window as any).opera
      const mobileUserAgent =
        /(android|bb\d+|meego).+mobile|avantgo|bada\/|blackberry|blazer|compal|elaine|fennec|hiptop|iemobile|ip(hone|od)|iris|kindle|lge |maemo|midp|mmp|mobile.+firefox|netfront|opera m(ob|in)i|palm( os)?|phone|p(ixi|re)\/|plucker|pocket|psp|series(4|6)0|symbian|treo|up\.(browser|link)|vodafone|wap|windows ce|xda|xiino/i.test(
          userAgent
        )
      const compactTaskModule = window.innerWidth <= 800 && window.innerHeight <= 900

      return mobileUserAgent || compactTaskModule
    }

    const isMobileBrowser = detectMobileBrowser()
    const isLikelyTeamsMobile = isMobileBrowser && detectTeamsContext()

    if (isLikelyTeamsMobile) {
      setIsMobile(true)
    }

    async function detectTeamsMobileClient() {
      try {
        const teamsSDK = await teamsSDKPromise
        if (!teamsSDK) {
          setIsMobile(isLikelyTeamsMobile)
          return
        }

        const context = await teamsSDK.app.getContext()
        const hostClientType = context.app?.host?.clientType?.toLowerCase()
        setIsMobile(hostClientType === 'android' || hostClientType === 'ios')
      } catch (error) {
        console.error('Failed to detect Teams platform:', error)
        setIsMobile(isLikelyTeamsMobile)
      }
    }

    detectTeamsMobileClient()
  }, [router.query, teamsSDKPromise])

  return isMobile
}
